// The Maker "worlds" — each craft opens its OWN page, organised into process
// CHAPTERS. Each chapter's media list is an INTENTIONAL SEQUENCE that alternates
// scale + subject + medium (wide scene / macro detail / video / person) so no two
// adjacent tiles ever look alike. Video entries: { video: "name" } -> /maker/vid/name.
// All imagery is Phaedra's real work; Phaedra (and Lulu) feature as the makers.

export const MAKER_PORTAL = {
  kicker: "Maker · In the garden",
  headline: ["Made with nature,", "made to be kept."],
  intro:
    "Phaedra makes slowly and by hand — colour drawn from petals and peel, shells strung from the shoreline, cloth pieced and pinned. She works with whatever the season gives her, and lets it lead. Nothing here is repeated; each piece is one of one, made to be kept.",
  heroImage: "dye-09",
};

export const MAKER_WORLDS = [
  {
    id: "dyeing",
    order: "01",
    title: "Natural Dyeing",
    subtitle: "Colour, coaxed from the garden",
    teaser: "Colour pulled from petals, peel and pressed leaves.",
    portal: "dye-09",
    hero: "dye-09",
    intro:
      "Every colour here begins as something living. Nothing is bought in a bottle — the garden and the kitchen do the work, and the cloth simply keeps a record of it.",
    chapters: [
      {
        kicker: "i",
        title: "Gather & bundle",
        text:
          "Petals and leaves pressed straight onto the cloth, then bound tight so each plant sits right against the fibre.",
        images: ["dye-02", { video: "dye-v4" }, "dye-03", "dye-t2", "dye-14", "dye-mix"],
      },
      {
        kicker: "ii",
        title: "Into the pot",
        text:
          "Simmered low with the garden's own colour until the plants give up their pigment and the cloth begins to bloom.",
        images: [{ video: "dye-v1" }, "dye-mix2", { video: "dye-v3" }, "dye-w1", "dye-t3", "dye-w2"],
      },
      {
        kicker: "iii",
        title: "The reveal",
        text:
          "The best part: unwrapped and lifted to the light. Each leaf and flower has left its own soft shadow behind — a print no two lengths will ever share.",
        images: ["dye-reveal", "dye-09", "dye-magenta", "dye-15", "dye-hold", "dye-hang2"],
      },
      {
        kicker: "iv",
        title: "Worn in the garden",
        text:
          "Made in a day with friends and family — Phaedra and Lulu in their hand-dyed pinks — the finished cloth laid out to dry across the lawn.",
        images: ["dye-scene", "dye-day", "dye-11", "dye-day2", "dye-12"],
      },
    ],
  },
  {
    id: "jewelry",
    order: "02",
    title: "Found-Shell Jewelry",
    subtitle: "Small treasures from the shore",
    teaser: "Shells and found things, wired into charms.",
    portal: "jewel-01",
    hero: "jewel-02",
    intro:
      "Nothing bought, everything found. Shells, sea-glass and small found things become quiet, wearable keepsakes — each one carrying the shape of the shore it came from.",
    chapters: [
      {
        kicker: "i",
        title: "Gather from the shore",
        text:
          "It begins at the tideline — shells picked up underfoot, carried home by the pocketful and washed clean, then laid out across the cloth to dry.",
        images: ["jewel-04", "shell-gather", "jewel-shore3", { video: "shell-v1" }, "shell-lift", "shell-dry"],
      },
      {
        kicker: "ii",
        title: "At the bench",
        text:
          "Back home, sorted by size and colour into the tray — the good ones set aside, the rest kept for another day — while the whole haul is turned over one more time.",
        images: ["shell-bench", "jewel-05", "shell-box", "jewel-01", { video: "shell-v2" }, "jewel-02"],
      },
      {
        kicker: "iii",
        title: "Set & wire by hand",
        text:
          "Each shell is sealed, cured under the lamp and wired entirely by hand — slow, close work, until a found thing becomes something to wear.",
        images: ["shell-cure", "jewel-t1", "shell-uv", "shell-paint", "jewel-03", "shell-pendant"],
      },
    ],
  },
  {
    id: "quilts",
    order: "03",
    title: "Scrap Quilts",
    subtitle: "Pieced from what remains",
    teaser: "Offcuts pieced into something to keep.",
    portal: "quilt-green-01",
    hero: "quilt-red-01",
    intro:
      "Pieced entirely from the offcuts of a friend's clothing line — scraps that would otherwise have been thrown away. Four quilts, four colourways, and no two blocks ever alike. Each one made to be handed down.",
    chapters: [
      {
        kicker: "i",
        title: "Pieced by hand",
        text:
          "Offcuts sorted by colour and pieced block by block — some by hand, some run through the machine — until a bag of scraps becomes a whole cloth.",
        images: [{ video: "quilt-v1" }, "quilt-make-1", "quilt-piece2", { video: "quilt-v2" }, "dye-couch", { video: "quilt-v3" }],
      },
      {
        kicker: "ii",
        title: "The Red — log cabin",
        text:
          "Rust, ochre and deep wine, built out in the log-cabin way — strips pieced round and round a centre until the whole thing glows.",
        images: ["quilt-red-01", "quilt-red-03", "quilt-red-02"],
      },
      {
        kicker: "iii",
        title: "The Blue",
        text:
          "Ink, indigo and teal set against pale grounds — a cooler, quieter riot, pieced from the bluest of the offcuts.",
        images: ["quilt-blue-01", "quilt-blue-03", "quilt-blue-02"],
      },
      {
        kicker: "iv",
        title: "The Green & Purple",
        text:
          "The loudest of the four — emerald, violet, tiger-stripe and bloom, thrown together until it hums.",
        images: ["quilt-green-01", "quilt-green-03", "quilt-green-02"],
      },
      {
        kicker: "v",
        title: "The Black & White",
        text:
          "Line, dot and ash, with the odd flash of gold — the most graphic of the set, worked into a concentric diamond.",
        images: ["quilt-bw-01", "quilt-bw-03", "quilt-bw-02"],
      },
    ],
  },
  {
    id: "studio",
    order: "04",
    title: "Custom Made in the Studio",
    subtitle: "The quiet, finer work",
    teaser: "Lace, silk and the slow, finer work.",
    portal: "p-1790",
    hero: "p-1798",
    intro:
      "Indoors, the slower craft — where the loose ideas of the garden are made precise. Lace under the scissors, silk pinned to the form, a shape coaxed out by hand over long afternoons.",
    chapters: [
      {
        kicker: "i",
        title: "Sketch & cut",
        text:
          "It starts on paper and then on the table — the line drawn, the cloth marked, the first careful cuts made.",
        images: ["p-2179", "p-1790", "p-1875"],
      },
      {
        kicker: "ii",
        title: "Pin to the form",
        text:
          "The pieces are pinned to the form and to the body, draped and re-draped until the shape sits the way it should — a one-of-one dress, custom made.",
        images: ["p-2067", "p-1952", "p-0742", "m-5131"],
      },
      {
        kicker: "iii",
        title: "Shape by hand",
        text:
          "Lace, silk and thread worked by hand — folded, knotted and finished, one quiet motion at a time.",
        images: ["p-1933", "p-1798", "p-2150"],
      },
      {
        kicker: "iv",
        title: "Made to measure",
        text:
          "The finished dresses, worn — cut and sewn by hand for one body and one day. From the fitting at home to the gown on the night.",
        images: ["gown-pink", "gown-coral", "gown-fit", "gown-duo"],
      },
      {
        kicker: "v",
        title: "For Lulu",
        text:
          "And the little ones, made to match — Lulu's own dresses, cut from the same care, twirled in until they're worn out.",
        images: ["dress-lulu-1", "gown-dance", "dress-lulu-2", "dress-lulu-3"],
      },
    ],
  },
  {
    id: "headpieces",
    order: "05",
    title: "Hats & Headpieces",
    subtitle: "Feather, silk and netting",
    teaser: "Feather and netting, coaxed into veils.",
    portal: "h-2223",
    hero: "h-2186",
    intro:
      "Feather, silk and birdcage netting, coaxed into hats, headpieces and veils — one-off pieces, built for a single head and a single day, and remembered long after.",
    chapters: [
      {
        kicker: "i",
        title: "The pieces",
        text:
          "Held to the light on their own — feather, bloom and net, each one a small, complete object before it is ever worn.",
        images: ["h-3733", "h-4140", "h-3575", "h-3788", "h-2186"],
      },
      {
        kicker: "ii",
        title: "Worn",
        text:
          "On the day, against the skin — the veil dropped, the feather set, the whole thing come alive on the head it was made for.",
        images: ["m-8140", "m-7974", "m-8616", "m-8929", "m-9150"],
      },
    ],
  },
];

export const makerWorld = (id) => MAKER_WORLDS.find((w) => w.id === id);
